import { FunctionsHttpError } from "@supabase/supabase-js";
import type { SynthesisDraft } from "../../domain/ai/contracts";
import type { EngineExecution } from "../../domain/engines/registry";
import type { SessionReport } from "./session";
import type { SupabaseClientLike } from "./persistence";

/** Must match DEFAULT_PROMPT_VERSION in the synthesize-narrative function. */
export const NARRATIVE_PROMPT_VERSION = "prompt-v2";

/**
 * User-facing Vietnamese messages per edge-function error code. Provider
 * raw text and JSON are never surfaced to the UI.
 */
const ERROR_MESSAGES_VI: Record<string, string> = {
  invalid_provider_output:
    "Dữ liệu luận giải chưa vượt qua bước kiểm tra nguồn, vui lòng thử lại.",
  provider_timeout:
    "Nhà cung cấp AI tạm thời không phản hồi, vui lòng thử lại sau.",
  provider_transport_error:
    "Nhà cung cấp AI tạm thời không phản hồi, vui lòng thử lại sau.",
  provider_upstream_error:
    "Nhà cung cấp AI tạm thời không phản hồi, vui lòng thử lại sau.",
  provider_unavailable:
    "Nhà cung cấp AI tạm thời không phản hồi, vui lòng thử lại sau.",
};

async function describeInvokeError(error: {
  message: string;
}): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body: unknown = await error.context.json();
      if (body && typeof body === "object" && "error" in body) {
        const code = String(body.error);
        return ERROR_MESSAGES_VI[code] ?? code;
      }
      return JSON.stringify(body);
    } catch {
      return `${error.message} (status ${error.context.status})`;
    }
  }
  return error.message;
}

type CompletedExecution = Extract<EngineExecution, { status: "completed" }>;

function isCompleted(
  execution: EngineExecution,
): execution is CompletedExecution {
  return execution.status === "completed";
}

export type NarrativeSynthesisResult =
  | Readonly<{ kind: "completed"; draft: SynthesisDraft }>
  | Readonly<{ kind: "blocked"; riskCode: string; messageVi: string }>
  | Readonly<{ kind: "error"; message: string }>;

type EdgeResponse = Readonly<{
  status?: "blocked" | "completed";
  riskCode?: string;
  messageVi?: string;
  draft?: SynthesisDraft;
  error?: string;
}>;

export async function requestNarrativeSynthesis(
  client: SupabaseClientLike,
  session: SessionReport,
): Promise<NarrativeSynthesisResult> {
  const executions = session.executions
    .filter(isCompleted)
    .map((execution) => execution.envelope);
  if (executions.length === 0) {
    return { kind: "error", message: "no_ready_engine_facts" };
  }

  const { data, error } = await client.functions.invoke<EdgeResponse>(
    "synthesize-narrative",
    {
      body: {
        question: session.request.question,
        inputHash: session.inputHash,
        executions,
      },
    },
  );
  if (error) {
    return { kind: "error", message: await describeInvokeError(error) };
  }
  if (data?.status === "blocked" && data.riskCode && data.messageVi) {
    return {
      kind: "blocked",
      riskCode: data.riskCode,
      messageVi: data.messageVi,
    };
  }
  if (data?.status === "completed" && data.draft) {
    return { kind: "completed", draft: data.draft };
  }
  return { kind: "error", message: data?.error ?? "empty_response" };
}
